import React, { useMemo } from "react";
import {
  Button,
  FormFieldSet,
  useFormHandler,
} from "../../../../../../components";

// Converts DD-MM-YYYY → MM-DD-YYYY for the datepicker.
// ISO strings and empty values pass through untouched.
const toPickerDate = (val) => {
  if (!val || val === "-") return "";
  if (typeof val === "string" && /^\d{2}-\d{2}-\d{4}$/.test(val)) {
    const [dd, mm, yyyy] = val.split("-");
    return `${mm}-${dd}-${yyyy}`;
  }
  return val;
};

// Strips dash placeholders so edit fields start empty instead of showing "-"
const nd = (val) => (val === null || val === undefined || val === "-" ? "" : val);

const EditPlacementDetails = ({ data, onClose, onSave }) => {
  const initialFormData = useMemo(() => ({
    jobTitle: nd(data?.employee?.placement?.jobTitle),
    startDate: toPickerDate(data?.employee?.placement?.startDate),
    endDate: toPickerDate(data?.employee?.placement?.endDate),
    billRate: nd(String(data?.employee?.placement?.billRate || "")),
    billType: data?.employee?.placement?.billType && data.employee.placement.billType !== "-" ? String(data.employee.placement.billType) : "",
    payRate: nd(String(data?.employee?.placement?.payRate || "")),
    jobCode: nd(data?.employee?.placement?.jobCode),
    offerSalary: nd(String(data?.employee?.placement?.offerSalary || "")),
    percentage: nd(String(data?.employee?.placement?.percentage || "")),
    targetAmount: nd(String(data?.employee?.placement?.targetAmount || "")),
    warrantyPeriod: data?.employee?.placement?.warrantyPeriod && data.employee.placement.warrantyPeriod !== "-" ? data.employee.placement.warrantyPeriod : "",
    placementType: data?.employee?.placement?.placementType ? String(data.employee.placement.placementType) : "1",
    commissionType: data?.employee?.placement?.commissionType ? String(data.employee.placement.commissionType) : "2",
  }), [data]);

  const { formData, errors, handleChange, validateAllFields, setFormData } =
    useFormHandler(initialFormData, {
      jobTitle: { required: "Job Title is required" },
      startDate: { type: "datepicker", required: "Start Date is required" },
      endDate: { type: "datepicker" },
      billRate: { type: "text" },
      billType: { type: "select" },
      payRate: { type: "text" },
      jobCode: { type: "text" },
      offerSalary: { type: "text" },
      percentage: { type: "text" },
      targetAmount: { type: "text" },
      warrantyPeriod: { type: "select" },
      placementType: { type: "select" },
      commissionType: { type: "select" },
    });

  const calculateValues = (field, value, currentData) => {
    const updated = { ...currentData, [field]: value };
    const isPermanent = Number(updated.placementType) === 2;
    const isPercentage = Number(updated.commissionType) === 2;

    // Special handling for placement type switch to preserve data integrity
    if (field === "placementType") {
      const oldTarget = Number(updated.targetAmount) || 0;
      const oldBill = Number(updated.billRate) || 0;
      const oldSalary = Number(updated.offerSalary) || oldBill;

      if (isPermanent) {
        // Switching to Permanent: Use Bill Rate as Salary, keep Target Amount
        updated.offerSalary = String(oldBill);
        updated.billRate = String(oldBill);
        // Keep target amount, percentage will be recalculated below
      } else {
        // Switching to Contract: Use Salary as Bill Rate, calculate Pay Rate to keep Target Amount
        updated.billRate = String(oldSalary);
        updated.payRate = String(Math.max(0, oldSalary - oldTarget));
        // targetAmount and percentage will be recalculated below
      }
    }

    const bill = Number(updated.billRate) || 0;
    const targetPct = Number(updated.percentage) || 0;
    const targetAmt = Number(updated.targetAmount) || 0;
    const salary = Number(updated.offerSalary) || 0;

    if (!isPermanent) {
      // Contract (IT) Logic: Target Amount = Bill Rate - Pay Rate
      const pay = Number(updated.payRate) || 0;
      updated.offerSalary = String(pay);
      updated.targetAmount = String(bill - pay);
      updated.percentage = String(bill > 0 ? Math.round(((bill - pay) / bill) * 100 * 100) / 100 : 0);
    } else {
      // Permanent (Non-IT) Logic: Bill Rate always equals Offered Salary
      if (field === "offerSalary") {
        updated.billRate = updated.offerSalary;
      } else if (field === "billRate") {
        updated.offerSalary = updated.billRate;
      } else {
        // Force sync for other field changes (like placementType or commissionType)
        updated.billRate = updated.offerSalary;
      }

      const baseForCalc = Number(updated.offerSalary) || 0;

      if (isPercentage) {
        // Percentage: Target Amount = Salary * (Percentage / 100)
        updated.targetAmount = String(Math.round(baseForCalc * (targetPct / 100)));
      } else {
        // Flat: Percentage = Target Amount / Salary
        updated.percentage = String(baseForCalc > 0 ? Math.round((targetAmt / baseForCalc) * 100 * 100) / 100 : 0);
      }
    }
    return updated;
  };

  const onFieldChange = (field, value) => {
    const label = value?.label || value;
    // Local fix: Convert numerical values to string for validation compatibility
    const valueForValidation = typeof label === "number" ? String(label) : label;
    const updatedData = calculateValues(field, valueForValidation, formData);
    setFormData(updatedData);
  };

  const handleSave = () => {
    const isValid = validateAllFields(); // Capture immediate return value

    const isPermanent = Number(formData.placementType) === 2;
    const isPercentage = Number(formData.commissionType) === 2;

    const finalErrors = {};
    if (!formData.jobTitle) finalErrors.jobTitle = "Job Title is required";
    if (!formData.startDate) finalErrors.startDate = "Start Date is required";
    if (isPermanent && !formData.offerSalary) finalErrors.offerSalary = "Offered Salary is required";
    if (isPermanent && isPercentage && !formData.percentage) finalErrors.percentage = "Percentage is required";

    // Re-check relevant errors from state (for long-lasting errors not caught by manual check)
    Object.keys(errors).forEach(key => {
      if (key === "offerSalary" && !isPermanent) return;
      if (key === "percentage" && (!isPermanent || !isPercentage)) return;
      if (errors[key]) finalErrors[key] = errors[key];
    });

    if (!isValid || Object.keys(finalErrors).length > 0) return;

    if (onSave) onSave(formData);
  };

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="grid grid-cols-4 gap-5">
        <FormFieldSet
          title="Job Title"
          type="text"
          placeholder="Job Title"
          value={formData.jobTitle}
          onChange={(value) => handleChange("jobTitle", value)}
          error={errors.jobTitle}
        />
        <FormFieldSet
          title="Job Code"
          type="text"
          placeholder="Job Code"
          value={formData.jobCode}
          onChange={(value) => onFieldChange("jobCode", value)}
          error={errors.jobCode}
        />
        <FormFieldSet
          title="Placement Type"
          type="select"
          options={[
            { label: "Contract", value: 1 },
            { label: "Permanent", value: 2 },
          ]}
          value={formData.placementType}
          onChange={(value) => onFieldChange("placementType", value)}
          error={errors.placementType}
        />
        <FormFieldSet
          title="Start Date"
          type="datepicker"
          placeholder="Start Date"
          value={formData.startDate}
          onChange={(value) => handleChange("startDate", value)}
          error={errors.startDate}
        />
        <FormFieldSet
          title="End Date"
          type="datepicker"
          placeholder="End Date"
          value={formData.endDate}
          onChange={(value) => handleChange("endDate", value)}
          error={errors.endDate}
        />
        {Number(formData.placementType) !== 2 && (
          <FormFieldSet
            title="Bill Rate"
            type="text"
            placeholder="Bill Rate"
            value={formData.billRate}
            onChange={(value) => onFieldChange("billRate", value)}
            error={errors.billRate}
          />
        )}
        <FormFieldSet
          title="Bill Type"
          type="select"
          options={[
            { label: "Hourly", value: "1" },
            { label: "Daily", value: "2" },
            { label: "Weekly", value: "3" },
            { label: "Monthly", value: "4" },
            { label: "Annual", value: "5" },
          ]}
          placeholder="Select Bill Type"
          value={formData.billType}
          onChange={(value) => handleChange("billType", value)}
          error={errors.billType}
        />
        {Number(formData.placementType) !== 2 && (
          <FormFieldSet
            title="Pay Rate"
            type="text"
            placeholder="Pay Rate"
            value={formData.payRate}
            onChange={(value) => onFieldChange("payRate", value)}
            error={errors.payRate}
          />
        )}
        <FormFieldSet
          title="Offered Salary"
          type="text"
          placeholder="Offered Salary"
          value={formData.offerSalary}
          onChange={(value) => onFieldChange("offerSalary", value)}
          error={Number(formData.placementType) === 2 ? errors.offerSalary : null}
          disabled={Number(formData.placementType) !== 2}
        />
        {Number(formData.placementType) === 2 && (
          <FormFieldSet
            title="Commission Type"
            type="select"
            options={[
              { label: "Flat", value: 1 },
              { label: "Percentage", value: 2 },
            ]}
            value={formData.commissionType}
            onChange={(value) => onFieldChange("commissionType", value)}
            error={errors.commissionType}
          />
        )}
        <FormFieldSet
          title="Percentage (%)"
          type="text"
          placeholder="Percentage"
          value={formData.percentage}
          onChange={(value) => onFieldChange("percentage", value)}
          error={(Number(formData.placementType) === 2 && Number(formData.commissionType) === 2) ? errors.percentage : null}
          disabled={Number(formData.placementType) !== 2 || Number(formData.commissionType) === 1}
        />
        <FormFieldSet
          title="Target Amount"
          type="text"
          placeholder="Target Amount"
          value={formData.targetAmount}
          onChange={(value) => onFieldChange("targetAmount", value)}
          error={errors.targetAmount}
          disabled={Number(formData.placementType) === 2 && Number(formData.commissionType) === 2}
        />
        <FormFieldSet
          title="Warranty Period"
          type="select"
          options={[
            { label: "30 days", value: 1 },
            { label: "60 days", value: 2 },
            { label: "90 days", value: 3 },
            { label: "120 days", value: 4 },
          ]}
          placeholder="Warranty Period"
          value={formData.warrantyPeriod}
          onChange={(value) => handleChange("warrantyPeriod", value)}
          error={errors.warrantyPeriod}
        />
      </div>
      <div className="flex gap-3 justify-end">
        <Button title="Cancel" variant="outlined" onClick={onClose} />
        <Button title="Save" onClick={handleSave} />
      </div>
    </div>
  );
};

export default EditPlacementDetails;
